import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, MapPin, Users, Eye, Filter } from "lucide-react";
import { api } from "@/lib/api";
import { format } from "date-fns";
import { AgeGroupBadge } from "@/components/ui/AgeGroupBadge";
import { BatchFilterPills } from "@/components/ui/BatchFilterPills";

interface Session {
  id: number;
  title: string;
  description: string | null;
  sessionType: string;
  ageGroup: string;
  location: string;
  startTime: string;
  endTime: string;
  coachName: string;
  maxPlayers: number | null;
  yesCount: number;
  noCount: number;
  maybeCount: number;
}

export default function CoachSessions() {
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<string | null>(null);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);

  // staleTime: 0 overrides the app-wide default (queryClient.ts sets
  // staleTime: Infinity globally) — this page shows per-session yes/no/maybe
  // RSVP counts, which change the moment a parent responds elsewhere. With
  // the global default, a coach tab left open on this page would keep
  // showing whatever counts were fetched on first load, no matter how many
  // parents RSVP afterward, until a full page reload. Same fix already
  // applied to BatchesPage.tsx's main query for the same reason.
  const { data, isLoading } = useQuery({
    queryKey: ["/api/coach/sessions", selectedAgeGroup],
    queryFn: () => {
      const params = selectedAgeGroup ? `?ageGroup=${encodeURIComponent(selectedAgeGroup)}` : '';
      return api.get(`/api/coach/sessions${params}`);
    },
    staleTime: 0,
  });

  const sessions: Session[] = data?.data || [];

  // Use sessions directly from API (already filtered)
  const filteredSessions = sessions;

  // Fixed age groups for filter
  const ageGroups = ["all", "5-8 years", "8+ years"];

  const formatDateTime = (dateStr: string) => {
    try {
      return format(new Date(dateStr), "MMM dd, yyyy • h:mm a");
    } catch {
      return dateStr;
    }
  };

  const formatTime = (dateStr: string) => {
    try {
      return format(new Date(dateStr), "h:mm a");
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="container mx-auto p-4 sm:p-6 space-y-4 sm:space-y-6 max-w-7xl" data-testid="coach-sessions-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="coach-sessions-heading">
          Coach Sessions
        </h1>
        <p className="text-slate-600 mt-1 text-sm sm:text-base">
          View and manage your training sessions
        </p>
      </div>

      {/* Filters */}
      <Card data-testid="coach-sessions-filters">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="h-4 w-4 text-slate-500" />
            <span className="text-sm font-semibold text-slate-900">Batch:</span>
            <BatchFilterPills
              value={selectedAgeGroup === null ? "all" : selectedAgeGroup as "5-8 years" | "8+ years"}
              onChange={(value: "all" | "5-8 years" | "8+ years") => setSelectedAgeGroup(value === "all" ? null : value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Sessions List */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
          <p className="text-slate-600 mt-4">Loading sessions...</p>
        </div>
      ) : filteredSessions.length === 0 ? (
        <Card data-testid="coach-sessions-empty">
          <CardContent className="py-12 text-center">
            <Calendar className="h-12 w-12 text-slate-400 opacity-40 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900">No sessions found</h3>
            <p className="text-slate-600 mt-1">
              {selectedAgeGroup === null 
                ? "No upcoming sessions scheduled"
                : `No sessions for ${selectedAgeGroup} batch`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="coach-sessions-list">
          {filteredSessions.map((session) => (
            <Card key={session.id} data-testid="coach-session-card">
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  {/* Session Info */}
                  <div className="flex-1 space-y-2">
                    <div className="flex items-start gap-2 flex-wrap">
                      <h3 className="text-lg font-bold text-slate-900" data-testid={`session-title-${session.id}`}>
                        {session.title}
                      </h3>
                      <AgeGroupBadge 
                        ageGroup={session.ageGroup}
                        testId="coach-session-age-group"
                      />
                      <Badge variant="secondary">{session.sessionType}</Badge>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-slate-600">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span data-testid={`session-date-${session.id}`}>
                          {formatDateTime(session.startTime)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>
                          {formatTime(session.startTime)} - {formatTime(session.endTime)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        <span data-testid={`session-location-${session.id}`}>
                          {session.location}
                        </span>
                      </div>
                    </div>

                    {/* Availability Counts */}
                    <div className="flex items-center gap-3 text-sm">
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4 text-slate-500" />
                        <span className="font-semibold text-slate-900">Responses:</span>
                      </div>
                      <span className="text-green-600 font-semibold">
                        {session.yesCount} Yes
                      </span>
                      <span className="text-slate-600 font-medium">
                        {session.maybeCount} Maybe
                      </span>
                      <span className="text-red-600 font-semibold">
                        {session.noCount} No
                      </span>
                    </div>
                  </div>

                  {/* View Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedSession(session)}
                    data-testid={`btn-view-session-${session.id}`}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Session Detail Modal/Drawer */}
      {selectedSession && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedSession(null)}
          data-testid="coach-session-detail-overlay"
        >
          <Card 
            className="max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="coach-session-detail-panel"
          >
            <CardContent className="p-6 space-y-4">
              {/* Header */}
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <h2 className="text-2xl font-bold" data-testid="detail-session-title">
                    {selectedSession.title}
                  </h2>
                  <div className="flex items-center gap-2">
                    <AgeGroupBadge ageGroup={selectedSession.ageGroup} />
                    <Badge variant="secondary">{selectedSession.sessionType}</Badge>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedSession(null)}
                  data-testid="btn-close-detail"
                >
                  ✕
                </Button>
              </div>

              {/* Details */}
              <div className="space-y-3 border-t pt-4">
                <div className="flex items-start gap-2">
                  <Calendar className="h-5 w-5 text-slate-500 mt-0.5" />
                  <div>
                    <p className="font-semibold text-slate-900">Date & Time</p>
                    <p className="text-sm text-slate-600" data-testid="detail-session-datetime">
                      {formatDateTime(selectedSession.startTime)}
                    </p>
                    <p className="text-sm text-slate-600">
                      Duration: {formatTime(selectedSession.startTime)} - {formatTime(selectedSession.endTime)}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <MapPin className="h-5 w-5 text-slate-500 mt-0.5" />
                  <div>
                    <p className="font-semibold text-slate-900">Location</p>
                    <p className="text-sm text-slate-600" data-testid="detail-session-location">
                      {selectedSession.location}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <Users className="h-5 w-5 text-slate-500 mt-0.5" />
                  <div>
                    <p className="font-semibold text-slate-900">Availability Responses</p>
                    <div className="flex gap-4 mt-1">
                      <span className="text-sm">
                        <span className="font-semibold text-green-600">{selectedSession.yesCount}</span> <span className="text-slate-600">Coming</span>
                      </span>
                      <span className="text-sm">
                        <span className="font-semibold text-slate-700">{selectedSession.maybeCount}</span> <span className="text-slate-600">Maybe</span>
                      </span>
                      <span className="text-sm">
                        <span className="font-semibold text-red-600">{selectedSession.noCount}</span> <span className="text-slate-600">Not Coming</span>
                      </span>
                    </div>
                  </div>
                </div>

                {selectedSession.description && (
                  <div className="border-t pt-3">
                    <p className="font-semibold text-slate-900 mb-1">Description</p>
                    <p className="text-sm text-slate-600" data-testid="detail-session-description">
                      {selectedSession.description}
                    </p>
                  </div>
                )}

                {selectedSession.maxPlayers && (
                  <div className="border-t pt-3">
                    <p className="font-semibold text-slate-900 mb-1">Max Players</p>
                    <p className="text-sm text-slate-600">
                      {selectedSession.maxPlayers} players
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
